import { useState, useEffect, useCallback } from "react";
import { Block } from "../../../document-writer/src/components/editor/Editor";
import { convertToBlocks } from "@/lib/utils/blockUtils";

export const useDocumentBlocks = (document: any) => {
  const [blocks, setBlocks] = useState<Block[]>([{ id: '1', type: 'text', content: '' }]);
  const [originalBlocks, setOriginalBlocks] = useState<Block[]>([{ id: '1', type: 'text', content: '' }]);
  const [hasChanges, setHasChanges] = useState(false);

  const extractBlocksFromDocument = useCallback((data: unknown): Block[] => {
    if (!data) return [{ id: '1', type: 'text', content: '' }];
    
    // Handle DocumentData[] structure (array of sections)
    if (Array.isArray(data)) {
      // If it's an array of DocumentData sections, extract blocks from the first section
      if (data.length > 0 && data[0] && typeof data[0] === 'object' && 'blocks' in data[0]) {
        const firstSection = data[0] as Record<string, unknown>;
        if (firstSection.blocks && Array.isArray(firstSection.blocks)) {
          return convertToBlocks(firstSection.blocks);
        }
      }
      // If it's a direct array of blocks (legacy format)
      else if (data.length > 0 && data[0] && typeof data[0] === 'object' && 'type' in data[0]) {
        return data as Block[];
      }
      // Empty array - return default block
      return [{ id: '1', type: 'text', content: '' }];
    }
    
    if (typeof data === 'object' && data !== null) {
      const docData = data as Record<string, unknown>;
      
      if (docData.blocks && Array.isArray(docData.blocks)) {
        // Convert saved block data back to Block format
        return convertToBlocks(docData.blocks);
      }
      
      if (docData.content && Array.isArray(docData.content)) {
        return docData.content as Block[];
      }
    }
    
    return [{ id: '1', type: 'text', content: '' }];
  }, []);

  const loadBlocks = useCallback((doc: any) => {
    const loadedBlocks = extractBlocksFromDocument(doc.data);
    
    // Remove duplicate blocks based on id
    const uniqueBlocks = loadedBlocks.filter((block, index, self) => 
      index === self.findIndex(b => b.id === block.id)
    );
    
    if (uniqueBlocks.length !== loadedBlocks.length) {
      console.warn('useDocumentBlocks: Found duplicate blocks, removing duplicates:', {
        original: loadedBlocks.length,
        unique: uniqueBlocks.length
      });
    }
    
    setBlocks(uniqueBlocks);
    setOriginalBlocks(uniqueBlocks);
    setHasChanges(false);
  }, [extractBlocksFromDocument]);

  const updateBlocks = useCallback((newBlocks: Block[]) => {
    // #region agent log
    if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useDocumentBlocks.ts:updateBlocks',message:'updateBlocks called',data:{blockCount:newBlocks?.length,firstBlockContent:newBlocks?.[0]?.content?.substring(0,80),callerStack:new Error().stack?.split('\n').slice(1,4).join(' | ')},timestamp:Date.now(),hypothesisId:'H-D'})}).catch(()=>{});}
    // #endregion
    setBlocks(newBlocks);
    setHasChanges(true);
  }, []);

  const markAsSaved = useCallback(() => {
    setOriginalBlocks(blocks);
    setHasChanges(false);
  }, [blocks]);

  const revertToSaved = useCallback(() => {
    setBlocks(originalBlocks);
    setHasChanges(false);
  }, [originalBlocks]);

  // Detect changes
  useEffect(() => {
    const hasBlockChanges = JSON.stringify(blocks) !== JSON.stringify(originalBlocks);
    setHasChanges(hasBlockChanges);
  }, [blocks, originalBlocks]);

  return {
    blocks,
    hasChanges,
    loadBlocks,
    updateBlocks,
    markAsSaved,
    revertToSaved,
  };
};
